/**
 * QMS (Quality Management System) Type Definitions
 * Based on the legacy AngularJS QMS module
 */

export type QmsTicketType = 'ticket' | 'capa' | 'client';

export type QmsTicketStatus = 'submitted' | 'in_progress' | 'ready_to_test' | 'complete' | 'validated_closed'
  | 'client_submitted' | 'pending_review' | 'approved' | 'closed'; // Client ticket statuses

export type QmsPriority = 1 | 2 | 3 | 4 | 5;

export type QmsDepartment =
  | 'product'
  | 'sales_marketing'
  | 'finance'
  | 'it'
  | 'hr'
  | 'quality'
  | 'operations';

export type QmsSubtype = 'problem' | 'improvement';

export type QmsClientTicketSubtype = 'issue' | 'request' | 'task' | 'installation';

export type QmsLineItemStatus = 'preparing' | 'ready' | 'planning' | 'in_progress' | 'complete' | 'cancelled';

/**
 * Client ticket line item (sensor/probe/equipment)
 */
export interface QmsLineItem {
  id: string;
  ticketId: string;
  mongoLineItemId?: string;
  status: QmsLineItemStatus;
  transitions: Array<{ from: string; to: string; date: string; user: string; user_name?: string }>;
  createdBy?: string;
  createdAt?: string;
  assignedTo?: string;
  scheduledAt?: string;
  sensorId?: string;
  sensorName?: string;
  streamId?: string;
  streamName?: string;
  transmitterId?: string;
  customid?: string;
  probeTypeId?: string;
  probeTypeName?: string;
  probeTypeHexId?: string;
  connectivityId?: string;
  connectivityName?: string;
  connectivityHexId?: string;
  equipmentManufacturer?: string;
  equipmentModel?: string;
  equipmentSerial?: string;
  application?: string;
  applicationType?: string;
  applicationUnit?: string;
  calibrationApplicationId?: string;
  calibrationRecordId?: string;
  certNumber?: string;
  installationQualificationId?: string;
  siteId?: string;
  siteName?: string;
  locationId?: string;
  locationName?: string;
  address?: string;
  usergroupId?: string;
  customerContactId?: string;
  customerContactName?: string;
  sku?: string;
  value?: number;
  linkedItems?: unknown[];
  options?: Record<string, unknown>;
  description?: string;
  comment?: string;
  files?: string[];
}

/**
 * Client ticket gateway (network equipment)
 */
export interface QmsGateway {
  id: string;
  ticketId: string;
  type: string;
  mac?: string;
  portsOpened?: boolean;
  staticip?: boolean;
  ip?: string;
  msk?: string;
  gtw?: string;
  dns?: string;
  dns2?: string;
  jack?: string;
  riser?: string;
  vlan?: string;
  room?: string;
  iccid?: string;
  paramId?: string;
  simProvider?: string;
  profiles?: unknown[];
}

/**
 * Client ticket shipping record
 */
export interface QmsShipping {
  id: string;
  ticketId: string;
  provider?: string;
  trackingNumber?: string;
  trackingLink?: string;
  status?: string;
  shippingDate?: string;
  deliveryDate?: string;
}

/**
 * Client ticket site visit
 */
export interface QmsSiteVisit {
  id: string;
  ticketId: string;
  date?: string;
  siteId?: string;
  address?: string;
  comment?: string;
  atekReps?: Array<{ id: string; name: string }>;
}

export type QmsSeverityLevel = 0 | 1 | 2; // 0: Info, 1: Standard, 2: Major

export type QmsApprovalStatus = 'pending' | 'approved' | 'rejected' | 'changes_requested';

export type QmsCeoReviewStatus = 'pending' | 'confirmed' | 'priority_adjusted';

export type QmsChangeApprovalStatus = 'pending' | 'approved' | 'rejected';

export type QmsRiskLevel = 'low' | 'medium' | 'high' | 'critical';

export type QmsChangeType = 'minor' | 'major' | 'critical';

export type QmsRcaMethod = 'five_whys' | 'ishikawa' | 'fault_tree' | 'other';

export type QmsQualityActionType = 'immediate' | 'corrective' | 'preventive' | 'implementation';

export type QmsQualityActionStatus = 'open' | 'complete' | 'verified';

export interface QmsQualityActionItem {
  id: string;
  type: QmsQualityActionType;
  description: string;
  owner_id: string;
  due_date: string;
  status: QmsQualityActionStatus;
  evidence?: string;
  completed_by?: string;
  completed_at?: string;
  verified_by?: string;
  verified_at?: string;
}

export interface QmsChangeApprovalRecord {
  user_id: string;
  signer_name?: string;
  decision: 'approved' | 'rejected';
  reason?: string;
  signed_at: string;
}

export type QmsQualitySignatureMeaning =
  | 'responsibility'
  | 'review'
  | 'approval'
  | 'implementation'
  | 'verification';

export type QmsWorkflowAction =
  | 'transition'
  | 'approve_change'
  | 'reject_change'
  | 'verify_effectiveness';

export interface QmsQualitySignature {
  id: string;
  ticketId: string;
  userId: string;
  username: string;
  signerName: string;
  meaning: QmsQualitySignatureMeaning;
  reason: string;
  action: QmsWorkflowAction;
  fromStatus: QmsTicketStatus;
  toStatus: QmsTicketStatus;
  signedAt: string;
}

export type QmsIntakeDecision = 'create_ticket' | 'no_action';

export type QmsWorkflowFilter =
  | 'all'
  | 'pending_qd_approval'
  | 'qd_approved'
  | 'pending_ceo_review'
  | 'ceo_confirmed';

/**
 * User reference (populated or unpopulated)
 */
export interface QmsUser {
  _id: string;
  firstname: string;
  lastname: string;
  email: string;
  username?: string;
}

/**
 * QMS Ticket - Main entity
 */
export interface QmsTicket {
  _id: string;
  type: QmsTicketType;
  organisation: string;

  // Ticket Number (auto-generated, unique)
  ticket_number?: number;
  ticket_id?: string; // Display ID in "QUAL-X" format

  // Account reference (V4 Account UUID - optional, links to V4 accounts table)
  account_id?: string;

  // Basic Info
  title: string;
  description?: string; // Rich text HTML
  priority: QmsPriority;
  status: QmsTicketStatus;
  department?: QmsDepartment;
  subtype?: QmsSubtype;
  severity_level: QmsSeverityLevel;
  tags?: string[];
  /** EOS team UUIDs the ticket is shared with. Mirrors `headlines.team_ids`.
   *  Empty array = visible to admins only. Backend filters non-admin reads
   *  via `team_members ?|` containment (mig 0203). */
  team_ids?: string[];

  // Dates
  due_date?: string; // ISO date string
  created_at: string;
  updated_at: string;
  completed_at?: string; // When ticket was marked complete

  // Assignment
  assigned_to?: QmsUser | string; // Populated or ID
  created_by?: QmsUser | string; // Populated or ID

  // QD Approval Workflow
  qd_approval_status?: QmsApprovalStatus;
  qd_approved_by?: QmsUser | string;
  qd_approved_at?: string;
  qd_approval_notes?: string;

  // CEO Review Workflow
  requires_ceo_review?: boolean;
  ceo_review_status?: QmsCeoReviewStatus;
  ceo_reviewed_by?: QmsUser | string;
  ceo_reviewed_at?: string;
  ceo_review_notes?: string;
  risk_level?: QmsRiskLevel;
  risk_assessment?: string; // Risk assessment reasoning (from AI or manual)

  // CAPA Flags
  needs_capa?: boolean; // True if this ticket requires a CAPA to be created
  parent_ticket?: QmsTicket | string; // For CAPA tickets - the ticket that spawned this CAPA
  related_tickets?: QmsRelatedTicket[]; // Related tickets (bidirectional links with relationship types)

  // Investigation (shared by tickets and CAPAs)
  root_cause_analysis?: string; // Rich text
  rca_method?: QmsRcaMethod;
  investigation_findings?: string; // Rich text
  investigation_date?: string;
  corrective_measures?: string; // Rich text - corrective measures
  preventive_measures?: string; // Rich text - preventive measures

  // Immediate Actions (tickets - part of Investigation & Immediate Actions section)
  immediate_actions?: string; // Rich text
  due_date_immediate?: string;

  // CAPA Actions (only for type: 'capa') - uses corrective_measures/preventive_measures above
  due_date_corrective?: string;
  due_date_preventive?: string;
  quality_action_items?: QmsQualityActionItem[];
  effectiveness_review_due_at?: string;
  effectiveness_review_status?: 'pending' | 'verified';
  effectiveness_review_metrics?: string;
  effectiveness_verified_by?: string;
  effectiveness_verified_at?: string;
  quality_signatures?: QmsQualitySignature[];

  // Change Control
  change_control_required?: boolean;
  change_type?: QmsChangeType;
  change_current_state?: string;
  change_proposed_state?: string;
  change_justification?: string;
  change_impact_assessment?: string; // Rich text
  risk_severity?: 1 | 2 | 3;
  risk_probability?: 1 | 2 | 3;
  risk_score?: number;
  change_approver_ids?: string[];
  change_approval_records?: QmsChangeApprovalRecord[];
  requires_sop_update?: boolean;
  linked_document_id?: string;
  implementation_outcome?: string; // Rich text
  implementation_review_notes?: string;
  training_applied?: boolean;
  change_approval_status?: QmsChangeApprovalStatus;
  change_approved_at?: string;
  change_approved?: boolean;
  change_approved_by?: QmsUser | string;
  change_approval_date?: string;

  // Validation
  validated_by?: QmsUser | string;
  validated_at?: string;
  validation_notes?: string;

  // Intake Decision (for Level 0 tickets)
  intake_decision?: QmsIntakeDecision;
  intake_decision_by?: QmsUser | string;
  intake_decision_at?: string;
  intake_decision_notes?: string;

  // AI Investigation
  ai_summary?: string; // Populated when AI findings have been applied
  ai_investigation_status?: QmsAiInvestigationStatus;

  // Position in column (for drag-drop)
  position?: number;

  // Notes/Comments (embedded array)
  notes?: QmsComment[];

  // PostgreSQL UUID (for activity log lookup)
  pg_uuid?: string;

  /** Array of Mongo user IDs subscribed to this ticket's updates. */
  followers?: string[];
  /** Backend-computed: true when the currently authenticated viewer is in the followers list. */
  viewerIsFollower?: boolean;

  // Client ticket fields (type: 'client')
  client_ticket_id?: string;
  client_name?: string;
  ticket_subtype?: QmsClientTicketSubtype;
  important?: number;
  urgent?: number;
  priority_product?: number;
  scheduled_at?: string;
  quote_number?: string;
  contract_number?: string;
  site_name?: string;
  value?: number;
  purchase_order?: string;
  invoice_number?: string;
  project_number?: string;
  skus?: string[];
  user_name?: string;
  email?: string;
  phone?: string;
  phone_extension?: string;
  customer_approval?: Record<string, unknown>;
  internal_notes?: string;
  internal_only?: boolean;
  solution?: string;
  organisation_name?: string;
  technical_manager?: string;
  it_manager?: string;
  contractual_manager?: string;
  department_managers?: string[];
  usergroup_ids?: string[];
  site_ids?: string[];
  location_ids?: string[];
  transmitter_ids?: string[];
  sensor_ids?: string[];
  status_update_date?: string;

  // Client ticket sub-resources
  line_items?: QmsLineItem[];
  gateways?: QmsGateway[];
  shipping?: QmsShipping[];
  site_visits?: QmsSiteVisit[];
}

/**
 * Column definition for Kanban board
 */
export interface QmsColumn {
  key: string;
  title: string;
  type: QmsTicketType;
  status: QmsTicketStatus;
}

/**
 * Board data structure
 */
export interface QmsBoard {
  [columnKey: string]: QmsTicket[];
}

/**
 * Board response from API
 */
export interface QmsBoardResponse {
  columns: QmsBoard;
  counts: {
    [key: string]: number;
  };
}

/**
 * Pending counts for summary badges
 */
export interface QmsPendingCounts {
  qd_approval: number;
  ceo_review: number;
}

/**
 * Ticket comment/note (embedded in ticket document)
 */
export interface QmsComment {
  _id: string;
  user: QmsUser | string; // User ID or populated user object
  user_name?: string; // Denormalized user name (firstname + lastname)
  content: string; // The actual comment text
  date: string; // Creation date (ISO string)
  edited_at?: string; // Edit timestamp (ISO string)
  deleted?: boolean; // Soft delete flag
}

/**
 * Related ticket link (embedded in ticket document)
 */
export interface QmsRelatedTicket {
  ticket: QmsTicket | string; // Ticket ID or populated ticket object
  relationship: QmsRelationshipType; // Relationship type
  linked_at: string; // ISO date string
  linked_by: QmsUser | string; // User ID or populated user object
}

/**
 * Relationship type for linked tickets
 */
export type QmsRelationshipType =
  | 'related_to'
  | 'duplicate_of'
  | 'caused_by'
  | 'blocks'
  | 'parent_of'
  | 'child_of';

/**
 * Ticket timeline event (audit trail)
 * Returned from the /timeline API endpoint
 */
export interface QmsTimelineEvent {
  type: string;
  timestamp: string;
  user?: {
    _id?: string;
    firstname?: string;
    lastname?: string;
    email?: string;
  };
  notes?: string;
  // Field change details
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
  // Comment-related
  noteId?: string;
  content?: string;
  isEdited?: boolean;
  // Workflow-related
  status?: string;
  decision?: string;
  needsCapa?: boolean;
  riskLevel?: string;
  priority?: number;
  changeType?: string;
  approvalStatus?: string;
  ticketId?: string;
  title?: string;
  // Attachment-related
  attachmentId?: string;
  // Additional metadata
  metadata?: {
    contentPreview?: string;
    deletedContentPreview?: string;
    [key: string]: unknown;
  };
}

/**
 * Move ticket payload
 */
export interface QmsMoveTicketPayload {
  status: QmsTicketStatus;
  position: number;
  organisation?: string;
}

export interface QmsWorkflowActionPayload {
  action: QmsWorkflowAction;
  meaning: QmsQualitySignatureMeaning;
  reason: string;
  username: string;
  password: string;
  organisation: string;
  target_status?: QmsTicketStatus;
  metrics?: string;
}

/**
 * Create/Update ticket payload
 */
export type QmsTicketPayload = Omit<
  QmsTicket,
  '_id' | 'created_at' | 'updated_at' | 'created_by'
>;

/**
 * QD Approval action payload
 */
export interface QmsQdApprovalPayload {
  action: 'approve' | 'reject' | 'request_changes';
  notes?: string;
  department?: QmsDepartment; // Optional reassignment during approval
  priority?: QmsPriority; // Optional reassignment during approval
  assigned_to?: string | null; // Optional user assignment during approval (MongoDB ObjectId, null to clear)
  requires_ceo_review?: boolean; // Only checked when action='approve' and value===true; escalates ticket to CEO review
}

/**
 * CEO Review action payload
 */
export interface QmsCeoReviewPayload {
  action: 'confirm' | 'adjust_priority';
  notes?: string;
  risk_level?: QmsRiskLevel;
  new_priority?: QmsPriority;
}

/**
 * Type Guards
 */

/** Check if a user field is populated (object) vs just an ID (string) */
export function isPopulatedUser(user: QmsUser | string | undefined): user is QmsUser {
  return typeof user === 'object' && user !== null && '_id' in user;
}

/** Check if a ticket field is populated (object) vs just an ID (string) */
export function isPopulatedTicket(ticket: QmsTicket | string | undefined): ticket is QmsTicket {
  return typeof ticket === 'object' && ticket !== null && '_id' in ticket;
}

/** Extract ID from potentially populated field */
export function extractId(field: any): string | undefined {
  if (!field) return undefined;
  if (typeof field === 'string') return field;
  if (typeof field === 'object' && '_id' in field) return field._id;
  return undefined;
}

/**
 * Create CAPA from Ticket payload
 */
export interface QmsCreateCapaPayload {
  title?: string;
  department?: QmsDepartment;
  priority?: QmsPriority;
  assigned_to?: string;
}

/**
 * QMS Trend Analysis Types
 */

export type QmsPatternType =
  | 'repeat_issue'
  | 'location_hotspot'
  | 'department_spike'
  | 'sla_breach_cluster'
  | 'capa_overdue'
  | 'approval_bottleneck';

/**
 * QMS Ticket Attachment
 */
export interface QmsAttachment {
  id: string;
  ticketId: string;
  filename: string;
  originalFilename: string;
  mimeType?: string;
  sizeBytes?: number;
  storagePath: string;
  uploadedById: string;
  uploadedByName: string;
  createdAt: string;
}

/**
 * QMS Pending File - for files selected during ticket creation (before ticketId exists)
 */
export interface QmsPendingFile {
  id: string; // Client-generated UUID for React key
  file: File; // The actual File object
  name: string; // Display name (file.name)
  size: number; // File size in bytes
  type: string; // MIME type
  preview?: string; // Object URL for image preview (blob:...)
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string; // Error message if upload failed
}

export type QmsPatternSeverity = 'low' | 'medium' | 'high' | 'critical';

/**
 * Base pattern interface
 */
interface QmsBasePattern {
  pattern_type: QmsPatternType;
  severity: QmsPatternSeverity;
  recommendation: string;
}

/**
 * Repeat Issue Pattern - Same root cause at same site
 */
export interface QmsRepeatIssuePattern extends QmsBasePattern {
  pattern_type: 'repeat_issue';
  site_id: string;
  site_name: string;
  cause: string;
  count: number;
  ticket_ids: string[];
  max_severity: number;
  first_occurrence: string; // ISO date
  last_occurrence: string; // ISO date
  timespan_days: number;
}

/**
 * Location Hotspot Pattern - Multiple issues at same location
 */
export interface QmsLocationHotspotPattern extends QmsBasePattern {
  pattern_type: 'location_hotspot';
  location_id: string;
  location_name: string;
  count: number;
  ticket_ids: string[];
  max_severity: number;
  timespan_days: number;
}

/**
 * Department Spike Pattern - Sudden increase in tickets
 */
export interface QmsDepartmentSpikePattern extends QmsBasePattern {
  pattern_type: 'department_spike';
  department: QmsDepartment;
  current_count: number;
  previous_count: number;
  increase_pct: number;
  timespan_days: number;
}

export type QmsTrendSlaStage =
  | 'intake_decision'
  | 'qd_approval'
  | 'ceo_review'
  | 'implementation'
  | 'validation';

export type QmsCapaActionType = 'immediate' | 'corrective' | 'preventive';

/**
 * SLA Breach Cluster Pattern - Multiple tickets warning/overdue in one workflow stage
 */
export interface QmsSlaBreachClusterPattern extends QmsBasePattern {
  pattern_type: 'sla_breach_cluster';
  stage: QmsTrendSlaStage;
  group_by: 'department';
  group_id: string;
  group_name: string;
  count: number;
  overdue_count: number;
  warning_count: number;
  max_hours_overdue: number;
  ticket_ids: string[];
  max_severity: number;
}

/**
 * CAPA Overdue Pattern - Missed CAPA action due dates
 */
export interface QmsCapaOverduePattern extends QmsBasePattern {
  pattern_type: 'capa_overdue';
  action_type: QmsCapaActionType;
  count: number;
  ticket_ids: string[];
  max_severity: number;
  oldest_due_at: string;
  max_days_overdue: number;
}

/**
 * Approval Bottleneck Pattern - Tickets stuck in QD or CEO review
 */
export interface QmsApprovalBottleneckPattern extends QmsBasePattern {
  pattern_type: 'approval_bottleneck';
  approval_stage: 'qd_approval' | 'ceo_review';
  count: number;
  ticket_ids: string[];
  max_severity: number;
  oldest_age_days: number;
}

/**
 * Union type for all pattern types
 */
export type QmsPattern =
  | QmsRepeatIssuePattern
  | QmsLocationHotspotPattern
  | QmsDepartmentSpikePattern
  | QmsSlaBreachClusterPattern
  | QmsCapaOverduePattern
  | QmsApprovalBottleneckPattern;

/**
 * Trend Summary Statistics
 */
export interface QmsTrendSummary {
  total_patterns: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

/**
 * Latest Trend Analysis Response
 */
export interface QmsTrendAnalysis {
  organisation: string;
  has_data: boolean;
  message?: string; // Present when has_data is false
  date?: string; // ISO date of analysis
  computed_at?: string; // ISO datetime when computed
  analysis_window_days?: number;
  total_tickets_analyzed?: number;
  patterns_detected?: QmsPattern[];
  summary?: QmsTrendSummary;
}

/**
 * Historical Trend Data Point
 */
export interface QmsTrendHistoryPoint {
  date: string; // ISO date
  computed_at: string; // ISO datetime
  summary: QmsTrendSummary;
  total_tickets_analyzed: number;
  pattern_count: number;
}

/**
 * Trend History Response
 */
export interface QmsTrendHistory {
  organisation: string;
  days: number;
  data: QmsTrendHistoryPoint[];
}

/**
 * Pattern List Response
 */
export interface QmsTrendPatterns {
  organisation: string;
  date: string; // ISO date
  computed_at: string; // ISO datetime
  patterns: QmsPattern[];
  total_patterns: number;
}

/**
 * Trend Summary Statistics Response
 */
export interface QmsTrendSummaryResponse {
  organisation: string;
  period_days: number;
  data_points: number;
  latest_analysis: {
    date: string;
    computed_at: string;
    patterns: number;
  };
  oldest_analysis: {
    date: string;
    patterns: number;
  };
  total_patterns_detected: number;
  avg_patterns_per_day: number;
  pattern_type_distribution: {
    repeat_issue: number;
    location_hotspot: number;
    department_spike: number;
    sla_breach_cluster: number;
    capa_overdue: number;
    approval_bottleneck: number;
  };
  severity_distribution: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
}

/**
 * Type guard for pattern types
 */
export function isRepeatIssuePattern(pattern: QmsPattern): pattern is QmsRepeatIssuePattern {
  return pattern.pattern_type === 'repeat_issue';
}

export function isLocationHotspotPattern(pattern: QmsPattern): pattern is QmsLocationHotspotPattern {
  return pattern.pattern_type === 'location_hotspot';
}

export function isDepartmentSpikePattern(pattern: QmsPattern): pattern is QmsDepartmentSpikePattern {
  return pattern.pattern_type === 'department_spike';
}

export function isSlaBreachClusterPattern(pattern: QmsPattern): pattern is QmsSlaBreachClusterPattern {
  return pattern.pattern_type === 'sla_breach_cluster';
}

export function isCapaOverduePattern(pattern: QmsPattern): pattern is QmsCapaOverduePattern {
  return pattern.pattern_type === 'capa_overdue';
}

export function isApprovalBottleneckPattern(pattern: QmsPattern): pattern is QmsApprovalBottleneckPattern {
  return pattern.pattern_type === 'approval_bottleneck';
}

/**
 * QMS SLA Tracking Types
 */

export type QmsSlaStatus = 'on_track' | 'warning' | 'overdue' | 'completed';

export type QmsSlaStage =
  | 'intake_decision'
  | 'qd_approval'
  | 'ceo_review'
  | 'implementation'
  | 'validation';

/**
 * SLA configuration for a specific stage
 */
export interface QmsSlaRule {
  stage: QmsSlaStage;
  hours: number; // Time allowed for this stage in hours
  warningThresholdPct: number; // Percentage of time remaining to trigger warning (e.g., 0.25 = 25%)
}

/**
 * SLA status for a ticket
 */
export interface QmsTicketSla {
  stage: QmsSlaStage;
  status: QmsSlaStatus;
  startTime: string; // ISO datetime when this stage started
  dueTime: string; // ISO datetime when this stage is due
  hoursRemaining: number; // Negative if overdue
  hoursElapsed: number;
  totalHours: number;
  percentComplete: number; // 0-100+ (can exceed 100 if overdue)
  displayText: string; // Human-readable text like "Due in 2h" or "Overdue 3d"
}

/**
 * ============================================================================
 * AI Investigation Types
 * ============================================================================
 */

export type QmsAiInvestigationStatus = 'pending' | 'in_progress' | 'completed' | 'failed';

export type QmsAiConfidence = 'low' | 'medium' | 'high';

/**
 * AI-identified potential root cause
 */
export interface QmsAiRootCause {
  cause: string;
  confidence: QmsAiConfidence;
  explanation: string;
  evidence?: string[];
}

/**
 * AI-identified related issue
 */
export interface QmsAiRelatedIssue {
  ticketId: string;
  title: string;
  relationship: string;
  relevanceScore?: number;
}

/**
 * AI risk assessment
 */
export interface QmsAiRiskAssessment {
  level: QmsRiskLevel;
  reasoning: string;
  factors?: string[];
}

/**
 * AI Investigation findings structure
 */
export interface QmsAiFindings {
  summary: string;
  potentialRootCauses: QmsAiRootCause[];
  relatedIssues: QmsAiRelatedIssue[];
  recommendedActions: string[];
  riskAssessment: QmsAiRiskAssessment;
  suggestedPriority?: QmsPriority;
  suggestedTags?: string[];
  codeAssessment?: {
    isCodeBug: boolean;
    confidence: number;
    reason: string;
    searchTerms: string[];
  };
  codeFix?: {
    status: 'pending' | 'in_progress' | 'pr_created' | 'not_applicable' | 'needs_review' | 'failed' | 'disabled';
    reason?: string;
    reasonCode?: string;
    nextStep?: string;
    sourceReview?: {
      explanation: string;
      evidence: Array<{ path: string; line: number }>;
    };
    repository?: string;
    pullRequestUrl?: string;
    pullRequestNumber?: number;
    branch?: string;
    baseSha?: string;
    validation?: 'review_and_ci_required';
  };
}

/**
 * AI Investigation record
 */
export interface QmsAiInvestigation {
  id: string;
  ticketId: string;
  status: QmsAiInvestigationStatus;
  startedAt?: string;
  completedAt?: string;
  modelId: string;
  modelVersion?: string;
  findings?: QmsAiFindings;
  inputTokens?: number;
  outputTokens?: number;
  errorMessage?: string;
  retryCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Extended QMS Ticket with AI investigation fields
 */
export interface QmsTicketWithAi extends QmsTicket {
  ai_investigation_id?: string;
  ai_investigation_status?: QmsAiInvestigationStatus;
  ai_summary?: string;
  ai_root_causes?: QmsAiRootCause[];
  ai_recommendations?: string[];
  ai_risk_level?: QmsRiskLevel;
  ai_suggested_tags?: string[];
}
